
import {TextFieldModule} from "@angular/cdk/text-field";
import {DOCUMENT, DatePipe, NgClass} from "@angular/common";
import {
    AfterViewInit,
    Component,
    ElementRef,
    HostBinding,
    HostListener,
    inject,
    Inject,
    Input,
    NgZone,
    OnDestroy,
    OnInit,
    Renderer2,
    ViewChild,
    ViewEncapsulation,
    computed,
    signal,
} from "@angular/core";
import {MatButtonModule} from "@angular/material/button";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatIconModule} from "@angular/material/icon";
import {MatInputModule} from "@angular/material/input";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatDialog, MatDialogModule} from "@angular/material/dialog";
import {MatProgressSpinnerModule} from "@angular/material/progress-spinner";
import {MatPaginatorModule, PageEvent} from "@angular/material/paginator";
import {MatTableModule} from "@angular/material/table";
import {DavesaScrollbarDirective} from "@davesa/directives/scrollbar";
import {Subject, Subscription} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {ReactiveFormsModule, UntypedFormControl} from "@angular/forms";
import { SortByIndexPipe } from "@davesa/pipes/sortByIndex.pipe";
import { ClinicNowV2Service } from "./clinic-now-v2.service";
import { ActivatedRoute, Router } from "@angular/router";
import { ng13ConfirmDialogComponent, ConfirmDialogModel } from "app/modules/davesa/studies/studies-builder/components/confirm-dialog/confirm-dialog.component";
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthDavesaV2Service } from 'app/core/auth-davesa/auth-davesa-v2.service';
import { UserRoleEnum } from 'app/core/enum/userRole.enum';
import { StudiesV2Service } from 'app/modules/davesa/apps/studies/studies-v2.service';
import { Page } from 'app/core/interfaces/page.model';
import { FinanceAdminService } from 'app/core/services/to-refactor/finance-admin.service';
import { FinanceStudyBudgetSettingsService } from 'app/core/services/to-refactor/finance-study-budget-settings.service';
import { getParticipantAvatarText } from 'app/core/util/participant-avatar.util';
import { ErrorHandlerUtil } from 'app/core/util/error-handler.util';
import { CheckInErrorDialogComponent } from 'app/shared/components/check-in-error-dialog/check-in-error-dialog.component';
import { CheckInErrorData } from 'app/core/interfaces/check-in-error.interface';
import { SignatureWorkflowService } from 'app/core/services/signature-workflow.service';
import { VisitsV2Service } from 'app/modules/davesa/apps/visits/visits-v2.service';
import { VisitStatus } from "app/core/enum/visit-status.enum";

@Component({
    selector: "clinic-now-list",
    templateUrl: "./clinic-now-list.component.html",
    styleUrls: ["./clinic-now-list.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [
        NgClass,
        MatIconModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        TextFieldModule,
        MatTooltipModule,
        MatDialogModule,
        MatProgressSpinnerModule,
        MatPaginatorModule,
        MatTableModule,
        ReactiveFormsModule,
        DatePipe,
    ],
    providers: [SortByIndexPipe]
})
export class ClinicNowListComponent implements OnInit, AfterViewInit, OnDestroy {
    private _clinicNowV2Service = inject(ClinicNowV2Service);
    private _dialog = inject(MatDialog);
    private _activatedRoute = inject(ActivatedRoute);
    private _router = inject(Router);
    private _snackBar = inject(MatSnackBar);
    private _authDavesaV2Service = inject(AuthDavesaV2Service);
    private _studiesV2Service = inject(StudiesV2Service);
    private _financeAdminService = inject(FinanceAdminService);
    private _financeStudyBudgetSettingsService = inject(FinanceStudyBudgetSettingsService);
    private _signatureWorkflowService = inject(SignatureWorkflowService);
    private _visitsV2Service = inject(VisitsV2Service);
    
    @ViewChild("clinicInput") clinicInput: ElementRef;
    @Input() iconTitle: string;
    @Input() oClinic: boolean = false;
    @Input() studyOid: string;
    @Input() isInSidePanel: boolean = false;
    
    subjectVisitOidFromUrl: string = "";

    // Subscriptions
    private queryParamsSubscription: Subscription;

    // Original data from service
    allClinicVisits = signal<any[]>([]);
    loading = this._clinicNowV2Service.loading;
    error = this._clinicNowV2Service.error;

    // Client-side pagination and filtering
    filteredVisits = signal<any[]>([]);
    paginatedVisits = signal<any[]>([]);
    page = signal<Page>(new Page());
    searchControl = new UntypedFormControl('');
    searchQuery = signal<string>('');

    // Role-based computed properties
    currentUserRole = computed(() => this._authDavesaV2Service.loginUserRole());
    isButtonsDisabled = computed(() => this.currentUserRole() === UserRoleEnum.monitor);

    // Computed values following study-assign-task pattern
    hasVisits = computed(() => this.allClinicVisits().length > 0);
    totalVisits = computed(() => this.filteredVisits().length);
    currentPageVisits = computed(() => this.paginatedVisits().length);
    visitsWithStatus = computed(() => {
        const visits = this.filteredVisits();
        return {
            scheduled: visits.filter(v => !v.arrived && !v.departed).length,
            inProgress: visits.filter(v => v.arrived && !v.departed).length,
            completed: visits.filter(v => v.departed).length
        };
    });

    private _mutationObserver: MutationObserver;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        @Inject(DOCUMENT) private _document: Document,
        private _elementRef: ElementRef,
        private _renderer2: Renderer2,
        private _ngZone: NgZone,
        private sortByIndexPipe: SortByIndexPipe,
    ) {
        // Initialize pagination with smaller page sizes to ensure pagination shows
        this.page.update(page => ({ 
            ...page, 
            size: 5, // Smaller page sizes to ensure pagination shows
            offset: 0,
            totalElements: 0,
            totalPages: 0,
            pageNumber: 0
        }));
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Decorated methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Host binding for component classes
     */
    @HostBinding("class") get classList(): any {
        return {};
    }

    /**
     * Resize on 'input' and 'ngModelChange' events
     *
     * @private
     */
    @HostListener("input")
    @HostListener("ngModelChange")
    private _resizeMessageInput(): void {
        // This doesn't need to trigger Angular's change detection by itself
        this._ngZone.runOutsideAngular(() => {
            setTimeout(() => {
                // Set the height to 'auto' so we can correctly read the scrollHeight
                this.clinicInput.nativeElement.style.height = "auto";

                // Get the scrollHeight and subtract the vertical padding
                this.clinicInput.nativeElement.style.height = `${this.clinicInput.nativeElement.scrollHeight}px`;
            });
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    async ngOnInit() {
        // Setup search debouncing
        this.setupSearchDebounce();

        // Load initial data
        await this.loadData();

        // Set up service data update listeners - this ensures all component instances
        // refresh when any action is performed from any component
        this._clinicNowV2Service.listenForUpdates()
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Refresh local data when service broadcasts changes
                this.refreshLocalData();
            });

        // Handle query parameters for deep linking
        this.queryParamsSubscription = this._activatedRoute.queryParams.subscribe((params) => {
            if (params['subject_visit_oid']) {
                this.subjectVisitOidFromUrl = params['subject_visit_oid'];
                // Handle opening the specific visit when data is loaded
                if (this.allClinicVisits()) {
                    this.handleDeepLinkVisit();
                }
            }
        });
    }

    /**
     * Setup search input debouncing
     */
    private setupSearchDebounce(): void {
        this.searchControl.valueChanges
            .pipe(
                takeUntil(this._unsubscribeAll),
                debounceTime(300),
                distinctUntilChanged()
            )
            .subscribe(query => {
                this.searchQuery.set(query || '');
                this.applyFilter();
                // Reset to first page when searching
                this.page.update(page => ({
                    ...page,
                    offset: 0,
                    pageNumber: 0
                }));
                this.applyPagination();
            });
    }

    /**
     * Apply client-side filtering
     */
    private applyFilter(): void {
        const query = this.searchQuery().toLowerCase().trim();
        const allVisits = this.allClinicVisits();

        if (!query) {
            this.filteredVisits.set(allVisits);
        } else {
            const filtered = allVisits.filter(visit => {
                const subjectId = visit.subject_id?.toLowerCase() || '';
                const visitName = visit.visit_name?.toLowerCase() || '';
                const studyNumber = visit.study_number?.toLowerCase() || '';
                
                return subjectId.includes(query) ||
                       visitName.includes(query) ||
                       studyNumber.includes(query);
            });
            this.filteredVisits.set(filtered);
        }

        // Update pagination info
        const totalElements = this.filteredVisits().length;
        const currentPage = this.page();
        const totalPages = Math.max(1, Math.ceil(totalElements / currentPage.size));
        
        this.page.update(page => ({
            ...page,
            totalElements: totalElements,
            totalPages: totalPages
        }));
    }

    /**
     * Apply client-side pagination
     */
    private applyPagination(): void {
        const currentPage = this.page();
        const filtered = this.filteredVisits();
        const startIndex = currentPage.offset;
        const endIndex = startIndex + currentPage.size;
        
        this.paginatedVisits.set(filtered.slice(startIndex, endIndex));
    }

    /**
     * Handle deep link to specific visit
     */
    private handleDeepLinkVisit(): void {
        if (this.subjectVisitOidFromUrl) {
            const clinicVisits = this.allClinicVisits();
            if (clinicVisits) {
                const targetVisit = clinicVisits.find(visit => 
                    visit.oid === this.subjectVisitOidFromUrl || 
                    visit.id === this.subjectVisitOidFromUrl
                );
                if (targetVisit) {
                    this.viewDetails(targetVisit);
                    this.subjectVisitOidFromUrl = ""; // Clear after handling
                }
            }
        }
    }

    /**
     * After view init
     */
    ngAfterViewInit(): void {
        // Fix for Firefox.
        //
        // Because 'position: sticky' doesn't work correctly inside a 'position: fixed' parent,
        // adding the '.cdk-global-scrollblock' to the html element breaks the navigation's position.
        // This fixes the problem by reading the 'top' value from the html element and adding it as a
        // 'marginTop' to the navigation itself.
        this._mutationObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                const mutationTarget = mutation.target as HTMLElement;
                if (mutation.attributeName === "class") {
                    if (mutationTarget.classList.contains("cdk-global-scrollblock")) {
                        const top = parseInt(mutationTarget.style.top, 10);
                        this._renderer2.setStyle(this._elementRef.nativeElement, "margin-top", `${Math.abs(top)}px`);
                    } else {
                        this._renderer2.setStyle(this._elementRef.nativeElement, "margin-top", null);
                    }
                }
            });
        });
        this._mutationObserver.observe(this._document.documentElement, {
            attributes: true,
            attributeFilter: ["class"],
        });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Disconnect the mutation observer
        this._mutationObserver.disconnect();

        // Unsubscribe from all subscriptions
        if (this.queryParamsSubscription) {
            this.queryParamsSubscription.unsubscribe();
        }

        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Load clinic data
     */
    async loadData(): Promise<void> {
        try {
            await this._clinicNowV2Service.refresh(this.studyOid);
            
            // Get data from service and store locally for client-side operations
            const serviceData = this._clinicNowV2Service.clinicBoard();
            
            this.allClinicVisits.set(serviceData || []);
            
            // Apply filtering and pagination
            this.applyFilter();
            this.applyPagination();
            
            // Handle deep linking after data is loaded
            if (this.subjectVisitOidFromUrl) {
                this.handleDeepLinkVisit();
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    }

    /**
     * Refresh data following study-assign-task pattern
     */
    refresh(): void {
        this.loadData();
    }

    /**
     * Refresh local data from service without triggering API call
     * Used when service broadcasts data changes from other components
     */
    refreshLocalData(): void {
        // Get the already updated data from the service
        const serviceData = this._clinicNowV2Service.clinicBoard();
        
        if (serviceData) {
            this.allClinicVisits.set(serviceData);
            
            // Apply filtering and pagination to the updated data
            this.applyFilter();
            this.applyPagination();
            
            // Handle deep linking if needed
            if (this.subjectVisitOidFromUrl) {
                this.handleDeepLinkVisit();
            }
        }
    }

    /**
     * Handle pagination changes following study-assign-task pattern
     */
    onPaginatorChange(event: PageEvent): void {
        const newOffset = event.pageIndex * event.pageSize;
        const newSize = event.pageSize;
        
        this.page.update(page => ({
            ...page,
            size: newSize,
            offset: newOffset,
            pageNumber: event.pageIndex
        }));
        
        // Apply manual pagination for card layout (both side panel and dashboard)
        this.applyPagination();
    }

    /**
     * Check in subject
     */
    async checkIn(visit: any): Promise<void> {
        try {
            // Check if already checked in (client-side validation)
            if (visit?.arrived || visit?.subject_clinic?.check_in_time) {
                this._snackBar.open('Subject is already checked in', 'Close', {
                    duration: 3000,
                    panelClass: ['snackbar-warning']
                });
                return;
            }
            
            // Validate required data and ensure we have the correct numeric ID
            // The API might return the ID as a string, so we need to parse it
            const subjectVisitId = parseInt(visit?.id);
            
            if (!subjectVisitId || isNaN(subjectVisitId)) {
                console.error('Invalid visit data for check-in:', {
                    visitId: visit?.id,
                    visitIdType: typeof visit?.id,
                    visitOid: visit?.oid,
                    fullVisitData: visit
                });
                throw new Error('Visit ID is required for check-in and must be a valid number');
            }

            const checkInData = {
                subject_visit_id: subjectVisitId
            };

            await this._clinicNowV2Service.checkIn(checkInData);
            
            // Show success message
            this._snackBar.open('Subject checked in successfully', 'Close', {
                duration: 3000,
                panelClass: ['snackbar-success']
            });

            
        } catch (error) {
            console.error('Error checking in subject:', error);
            
            // Extract detailed error message using ErrorHandlerUtil
            const errorMessage = ErrorHandlerUtil.extractErrorMessage(error, 'general') || error?.message || '';
            
            // Show error dialog
            this.showCheckInErrorDialog(visit, errorMessage, error);
        }
    }

    /**
     * Show check-in error dialog
     */
    private async showCheckInErrorDialog(visit: any, errorMessage: string, errorDetails?: any): Promise<void> {
        const studyOid = visit?.study?.oid || visit?.study_oid;
        
        let workflowSteps: any[] = [];
        
        // Parse error response to get personnel issues
        let expiredPersonnel: any[] = [];
        try {
            const reasons = errorDetails?.error?.data?.reasons || errorDetails?.data?.reasons;
            if (reasons) {
                expiredPersonnel = [
                    ...(reasons.expired_personnel || []),
                    ...(reasons.missing_roles || []),
                    ...(reasons.removing_personnel || []),
                    ...(reasons.removed_personnel || []),
                    ...(reasons.inactive_personnel || []),
                    ...(reasons.deleted_personnel || [])
                ];
            }
        } catch (parseError) {
            console.error('Error parsing error details:', parseError);
        }
        
        try {
            const workflow = await this._signatureWorkflowService.getWorkflowConfiguration(studyOid);
            
            if (workflow && workflow.steps) {
                const roleIds = this._signatureWorkflowService.extractRoleIdsFromWorkflow(workflow);

                if (roleIds.length > 0) {
                    const personnel = await this._visitsV2Service.getPersonnelByRoles(studyOid, roleIds);

                    workflowSteps = this._signatureWorkflowService.buildWorkflowStepsWithPersonnel(
                        workflow,
                        personnel,
                        visit,
                        (person) => this.getFullName(person),
                        (stepId: string, expiredPersonnelList: any[]) => {
                            // Try matching by signature_step_id first
                            let matchingError = expiredPersonnelList.find((err: any) => 
                                err.signature_step_id === stepId
                            );
                            
                            // Fallback to matching by step label if not found
                            if (!matchingError && workflow?.steps) {
                                const step = workflow.steps.find(s => s.oid === stepId);
                                if (step) {
                                    matchingError = expiredPersonnelList.find((err: any) => 
                                        err.step_label === step.label
                                    );
                                }
                            }
                            
                            return matchingError?.reason || '';
                        },
                        expiredPersonnel
                    );
                }
            }
        } catch (error) {
            console.error('Error loading personnel:', error);
        }

        const dialogData: CheckInErrorData = {
            subjectId: visit?.subject_id || '',
            visitName: visit?.visit_name || '',
            studyNumber: visit?.study_number || '',
            visitDate: visit?.appointment_start_date || '',
            visitEndDate: visit?.appointment_end_date || '',
            visitOid: visit?.oid,
            studyOid: studyOid,
            targetStatus: VisitStatus.CLINIC,
            errorMessage: errorMessage,
            errorDetails: errorDetails,
            workflowSteps: workflowSteps
        };

        const dialogRef = this._dialog.open(CheckInErrorDialogComponent, {
            width: '650px',
            maxWidth: '90vw',
            maxHeight: '90vh',
            disableClose: false,
            data: dialogData
        });

        dialogRef.afterClosed().subscribe(async (result) => {
            if (result?.success && result?.retry) {
                try {
                    // Dialog has already updated personnel and status, just refresh data
                    await this._clinicNowV2Service.refresh(studyOid);
                    
                    // Update local data immediately after refresh
                    const serviceData = this._clinicNowV2Service.clinicBoard();
                    this.allClinicVisits.set(serviceData || []);
                    
                    // Apply filtering and pagination to updated data
                    this.applyFilter();
                    this.applyPagination();
                } catch (error) {
                    console.error('Error refreshing clinic data:', error);
                }
            }
        });
    }

    /**
     * Get full name from personnel object
     */
    private getFullName(person: any): string {
        const parts = [
            person.firstName,
            person.middleName,
            person.lastName
        ].filter(p => p && p.trim());

        let fullName = parts.join(' ');
        if (person.suffixName?.name) {
            fullName += `, ${person.suffixName.name}`;
        }
        return fullName;
    }

    /**
     * Check out subject
     */
    async checkOut(visit: any): Promise<void> {
        try {
            // Get the subject clinic OID from the visit data
            const subjectClinicOid = visit?.subject_clinic?.oid || visit?.subjectInClinic?.oid;
            
            if (!subjectClinicOid) {
                console.error('Missing subject clinic OID for checkout:', visit);
                this._snackBar.open('Cannot check out: Missing clinic data', 'Close', {
                    duration: 5000,
                    panelClass: ['snackbar-error']
                });
                return;
            }

            const checkoutResponse = await this._clinicNowV2Service.checkOut({ oid: subjectClinicOid });
            
            // Show success message
            this._snackBar.open('Subject checked out successfully', 'Close', {
                duration: 3000,
                panelClass: ['snackbar-success']
            });

            // FIXME: This implementation just keep the same logic as legacy code. 
            // FIXME: In future, all billable item generation should be done at API server-side.
            
            // Try to generate billable item for finance integration
            // This will be handled in backend
            // try {
            //     // Check if study has finance enabled before generating billable item
            //     if (visit.study?.is_finance) {
            //         // Match the exact legacy behavior: first check budget settings, then generate billable item
            //         this._financeStudyBudgetSettingsService.getBudgetSettings(visit.study.oid).subscribe({
            //             next: (budget) => {
            //                 // Check if the new visit status (after checkout) matches the invoice_event
            //                 // Use the actual status returned from the checkout API
            //                 const newVisitStatus = checkoutResponse?.visit_status;
            //                 if (newVisitStatus === budget.data.invoice_event) {
            //                     // Use the exact same API endpoint and parameters as legacy code
            //                     this._financeAdminService.generateVisitBillableItem({
            //                         subject_visit_oid: visit.oid,
            //                         study_id: visit.study?.id || visit.study_id
            //                     }).subscribe({
            //                         next: (result) => {
            //                             if (result && result.data && Object.keys(result.data).length > 0) {
            //                                 console.log('Billable item generated successfully:', result);
            //                             } else {
            //                                 console.log('Billable item not generated - visit may not be in required status yet');
            //                             }
            //                         },
            //                         error: (error) => {
            //                             console.warn('Finance billable item generation failed:', error);
            //                         }
            //                     });
            //                 }
            //             },
            //             error: (error) => {
            //                 console.warn('Failed to get budget settings:', error);
            //             }
            //         });
            //     }
            // } catch (financeError) {
            //     console.warn('Finance billable item generation failed:', financeError);
            //     // Don't fail the checkout if billable item generation fails
            // }

            
        } catch (error) {
            console.error('Checkout failed:', error);
            this._snackBar.open(
                error.message || 'Failed to check out subject', 
                'Close', 
                {
                    duration: 5000,
                    panelClass: ['snackbar-error']
                }
            );
        }
    }

    /**
     * View visit details
     */
    async viewDetails(visit) {
        const participantOid = visit.subject_oid || visit.subject?.oid;
        const visitOid = visit.oid || visit.visit_oid || visit.id;
        if (participantOid && visitOid) {
            this._router.navigate(["/apps/visits", participantOid, visitOid]);
        } else {
            console.error('Missing visit or subject information for navigation:', visit);
        }
    }

    /**
     * Open subject details for completed visits
     */
    openParticipant(visit: any): void {
        const participantOid = visit.subject_oid || visit.subject?.oid;
        if (participantOid) {
            this._router.navigate(["/apps/participants", participantOid]);
        } else {
            console.error('Missing subject information for navigation:', visit);
        }
    }

    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): string | number {
        return item.id || index;
    }

    /**
     * Get grid tooltip text (migrated from legacy)
     */
    getGridToolTip(rowData: any, type: string): string | false {
        if (type === "clinic_in") {
            if (!rowData.subject_clinic?.check_in_time && !rowData.arrived) {
                return "Check Subject into Clinic";
            } else {
                return false;
            }
        } else if (type === "clinic_view") {
            return "View Visit Details";
        } else if (type === "clinic_out") {
            return "Check Out Participant";
        } else if (type === "clinic_done") {
            return "View Participant Details";
        }
        return false;
    }

    /**
     * Get grid icon based on visit status (migrated from legacy)
     */
    getGridIcon(rowData: any, type: string): string | false {
        if (type === "clinic_in") {
            if (!rowData.subject_clinic?.check_in_time && !rowData.arrived) {
                return "clinic_in";
            } else {
                return false;
            }
        } else if (type === "clinic_view") {
            return "clinic_view";
        } else if (type === "clinic_out") {
            if ((rowData.subject_clinic?.check_in_time || rowData.arrived) && 
                (!rowData.subject_clinic?.check_out_time && !rowData.departed)) {
                return "clinic_out";
            }
            return false;
        } else if (type === "clinic_done") {
            if ((rowData.subject_clinic?.check_in_time || rowData.arrived) && 
                (rowData.subject_clinic?.check_out_time || rowData.departed)) {
                return "clinic_done";
            }
            return false;
        }
        return false;
    }

    /**
     * Get visit status class for styling
     */
    getVisitStatusClass(visit: any): string {
        if (visit.departed || visit.subject_clinic?.check_out_time) {
            return 'bg-green-100 text-green-800';
        } else if (visit.arrived || visit.subject_clinic?.check_in_time) {
            return 'bg-yellow-100 text-yellow-800';
        } else {
            return 'bg-gray-100 text-gray-800';
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    visitStatusClass(visit: any): string {
        if (visit.departed || visit.subject_clinic?.check_out_time) {
            return 'bg-green-100 text-green-800';
        } else if (visit.arrived || visit.subject_clinic?.check_in_time) {
            return 'bg-yellow-100 text-yellow-800';
        } else {
            return 'bg-gray-100 text-gray-800';
        }
    }

    getParticipantAvatar(subject: any) {
        return getParticipantAvatarText(subject);
    }
}
